/* eslint-disable @typescript-eslint/no-use-before-define */
import "core-js/stable";
import "regenerator-runtime/runtime";
import { FC } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import type { Persistor } from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import * as Sentry from "@sentry/react";
import { initI18next, localeNameSpaces } from "@payping/utils/localeUtils";
import { configureStore } from "@payping/redux";
import { AppStoreStates, reduxConfig } from "@/redux/configs";
import App from "@/App";
import { AppErrorBoundary } from "@/AppErrorBoundary";
import { setToken } from "@/utils";
import { localeResources } from "@/assets/locales/resources";
import "./app-config";
import "./main.scss";
import "./apiConfigs";

const initToken = () => {
  const envToken = process.env.REACT_APP_TOKEN;
  const { auth, organization } = store.getState();
  const reduxToken = organization.token || auth.token;

  if (envToken) {
    setToken.forAll(store.dispatch, envToken);
  } else if (reduxToken) {
    setToken.forApi(reduxToken);
  }
};

// eslint-disable-next-line react-refresh/only-export-components
const Root: FC = () => (
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor as Persistor}>
      <App />
    </PersistGate>
  </Provider>
);

const renderAppOnRehydrate = async (): Promise<void> => {
  initToken();
  await initI18next(localeNameSpaces.Wrapper, localeResources);
  const ProfiledApp = Sentry.withProfiler(Root);

  const root = createRoot(document.getElementById("root") as HTMLDivElement);
  root.render(
    <AppErrorBoundary>
      <ProfiledApp />
    </AppErrorBoundary>,
  );
};

export const { store, persistor } = configureStore<AppStoreStates>({
  ...reduxConfig,
  onRehydrated: renderAppOnRehydrate,
});
